import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, LayoutGrid, List, FileText, X } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { FilterBar, ExportMenu, MODULE_CONFIG } from '../shared';
import { Select } from '../../../../../components/ui/Select';
import { QuestionDetailPanel } from './QuestionDetailPanel';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { PipelineBatchResult } from '../../../../../governance';
import { deriveDisplaySeverity } from '../../../../../governance';
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry';
/**
 * Look up the human-readable Dutch label for a question type.
 * Falls back to "Onbekend" when the type is the 'unknown' sentinel
 * emitted by buildUnifiedRows (question lookup miss), or returns
 * the raw key when a registry entry is missing (defensive fallback).
 */
function getQuestionTypeLabel(type: string): string {
  if (type === 'unknown') return 'Onbekend';
  const entry = (QUESTION_TYPE_REGISTRY as Record<string, {
    label?: string;
  }>)[type];
  return entry?.label ?? type;
}
interface QuestionsTabProps {
  rows: UnifiedQuestionRow[];
  batchResult: PipelineBatchResult | null;
  isRunning: boolean;
  initialFilter?: string;
}
type VerdictFilter = 'all' | 'PASS' | 'REVIEW_NEEDED' | 'FAIL';
type IssueFilter = 'all' | 'critical' | 'blockers' | 'review' | 'warnings' | 'info';
type ViewMode = 'cards' | 'table';
export function QuestionsTab({
  rows,
  batchResult,
  isRunning,
  initialFilter
}: QuestionsTabProps) {
  const [search, setSearch] = useState(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>(initialFilter === 'blockers' ? 'FAIL' : 'all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  // Quick filters
  const [onlySeedIssues, setOnlySeedIssues] = useState(false);
  const [onlyVisualIssues, setOnlyVisualIssues] = useState(false);
  const [onlyPedagogyIssues, setOnlyPedagogyIssues] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Search
      if (search && !row.questionId.toLowerCase().includes(search.toLowerCase()) && !row.familyId.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Module
      if (moduleFilter !== 'all' && row.moduleCode !== moduleFilter) {
        return false;
      }
      // Verdict
      if (verdictFilter !== 'all' && row.verdict !== verdictFilter) {
        return false;
      }
      // Issue Type — Phase 1: 5-level display severity filter
      if (issueFilter !== 'all') {
        const hasMatching = row.pipelineIssues.some(i => {
          const ds = deriveDisplaySeverity(i.kind, i.severity);
          if (issueFilter === 'critical') return ds === 'critical';
          if (issueFilter === 'blockers') return ds === 'critical' || ds === 'blocker';
          if (issueFilter === 'review') return ds === 'review';
          if (issueFilter === 'warnings') return ds === 'coach';
          if (issueFilter === 'info') return ds === 'info';
          return false;
        });
        if (!hasMatching) return false;
      }
      // Question Type
      if (typeFilter !== 'all' && row.questionType !== typeFilter) {
        return false;
      }
      // Quick Filters
      if (onlySeedIssues && row.seedWarnings.length === 0) return false;
      if (onlyVisualIssues && !row.hasVisualProblems) return false;
      if (onlyPedagogyIssues && !row.pipelineIssues.some(i => i.domain === 'pedagogy')) return false;
      return true;
    });
  }, [rows, search, moduleFilter, verdictFilter, issueFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  // Detect "scoped to single seed file" — happens when user deep-links from
  // the Seed Prioritizer card on the Overzicht tab. We surface this clearly
  // so the admin knows they're currently triaging one seed at a time.
  const familyContext = useMemo(() => {
    if (!search || filteredRows.length === 0) return null;
    const uniqueFamilies = new Set(filteredRows.map(r => r.familyId));
    if (uniqueFamilies.size !== 1) return null;
    const familyId = filteredRows[0].familyId;
    // Only show the banner if the search term actually targets the family
    // (case-insensitive substring match). Otherwise this is just coincidence.
    if (!familyId.toLowerCase().includes(search.toLowerCase())) return null;
    const blockerCount = filteredRows.reduce((sum, r) => sum + r.blockerCount, 0);
    const warningCount = filteredRows.reduce((sum, r) => sum + r.warningCount, 0);
    const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
    return {
      familyId,
      moduleCode: filteredRows[0].moduleCode,
      total: filteredRows.length,
      failCount,
      blockerCount,
      warningCount
    };
  }, [filteredRows, search]);
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, moduleFilter, verdictFilter, issueFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, page]);
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const passCount = filteredRows.filter(r => r.verdict === 'PASS').length;
  const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
  const reviewCount = filteredRows.filter(r => r.verdict === 'REVIEW_NEEDED').length;
  // Build type-filter options from the registry, sorted by Dutch label, and
  // include an "Onbekend" option ONLY when at least one row actually has the
  // 'unknown' fallback — that way the user can find broken rows without the
  // dropdown showing a phantom option in healthy datasets.
  const typeOptions = useMemo(() => {
    const registryEntries = Object.entries(QUESTION_TYPE_REGISTRY as Record<string, {
      label?: string;
    }>).map(([value, cfg]) => ({
      value,
      label: cfg.label ?? value
    })).sort((a, b) => a.label.localeCompare(b.label, 'nl'));
    const hasUnknown = rows.some(r => r.questionType === 'unknown');
    const options = [{
      value: 'all',
      label: 'Alle vraagtypes'
    }, ...registryEntries];
    if (hasUnknown) options.push({
      value: 'unknown',
      label: 'Onbekend'
    });
    return options;
  }, [rows]);
  if (isRunning) {
    return <div className="py-12 flex flex-col items-center justify-center space-y-4" data-id="element-5448">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-5449"></div>
        <p className="text-gray-500 font-medium" data-id="element-5450">Data laden en valideren...</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-5451">
      {/* Family-scope banner — visible when filtered to a single seed file */}
      {familyContext && <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-orange-200 bg-orange-50/70" data-id="element-5452">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${MODULE_CONFIG[familyContext.moduleCode]?.bgClass ?? 'bg-gray-400'}`} data-id="element-5453" />
          <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" data-id="element-5454" />
          <div className="min-w-0 flex-1" data-id="element-5455">
            <p className="text-sm font-semibold text-orange-900 truncate" data-id="element-5456">
              Bekijk seed:{' '}
              <span className="font-mono" data-id="element-5457">{familyContext.familyId}</span>
            </p>
            <p className="text-xs text-orange-800/80 mt-0.5" data-id="element-5458">
              {familyContext.total}{' '}
              {familyContext.total === 1 ? 'vraag' : 'vragen'}
              {familyContext.failCount > 0 && ` · ${familyContext.failCount} afgekeurd`}
              {familyContext.blockerCount > 0 && ` · ${familyContext.blockerCount} blocker${familyContext.blockerCount === 1 ? '' : 's'}`}
              {familyContext.warningCount > 0 && ` · ${familyContext.warningCount} verbeterpunt${familyContext.warningCount === 1 ? '' : 'en'}`}
            </p>
          </div>
          <button type="button" onClick={() => setSearch('')} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 hover:text-orange-900 bg-white border border-orange-200 hover:bg-orange-100 rounded-lg transition-colors flex-shrink-0" data-id="element-5459">
            <X className="w-3 h-3" data-id="element-5460" />
            Wis filter
          </button>
        </div>}

      {/* Filters */}
      <FilterBar moduleFilter={moduleFilter} onModuleFilter={setModuleFilter} search={search} onSearch={setSearch} data-id="element-5461">
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} options={typeOptions} size="sm" fullWidth={false} data-id="element-5462" />
        <Select value={verdictFilter} onChange={e => setVerdictFilter(e.target.value as VerdictFilter)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'PASS',
        label: 'Geslaagd (PASS)'
      }, {
        value: 'REVIEW_NEEDED',
        label: 'Review (REVIEW)'
      }, {
        value: 'FAIL',
        label: 'Afgekeurd (FAIL)'
      }]} size="sm" fullWidth={false} data-id="element-5463" />
        <Select value={issueFilter} onChange={e => setIssueFilter(e.target.value as IssueFilter)} options={[{
        value: 'all',
        label: 'Alle bevindingen'
      }, {
        value: 'critical',
        label: '🔴 Heeft kritieke'
      }, {
        value: 'blockers',
        label: '🟠 Heeft blockers'
      }, {
        value: 'review',
        label: '🟡 Heeft review'
      }, {
        value: 'warnings',
        label: '🔵 Heeft coach'
      }, {
        value: 'info',
        label: '⚪ Heeft info'
      }]} size="sm" fullWidth={false} data-id="element-5464" />
      </FilterBar>

      {/* Quick Filters & Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-5465">
        <div className="flex flex-wrap items-center gap-2" data-id="element-5466">
          <button onClick={() => setOnlySeedIssues(!onlySeedIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlySeedIssues ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5467">
            Seed Issues
          </button>
          <button onClick={() => setOnlyVisualIssues(!onlyVisualIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyVisualIssues ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5468">
            Visual Issues
          </button>
          <button onClick={() => setOnlyPedagogyIssues(!onlyPedagogyIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyPedagogyIssues ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5469">
            Pedagogie Issues
          </button>

          <div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block" data-id="element-5470"></div>

          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-5471">
            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Tabelweergave" data-id="element-5472">
              <List className="w-4 h-4" data-id="element-5473" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Kaartweergave" data-id="element-5474">
              <LayoutGrid className="w-4 h-4" data-id="element-5475" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm" data-id="element-5476">
          <div className="flex items-center gap-3 text-gray-600" data-id="element-5477">
            <span className="font-semibold text-gray-900" data-id="element-5478">
              {filteredRows.length}
            </span>{' '}
            vragen
            <span className="text-emerald-600 font-medium" data-id="element-5479">
              {passCount} geslaagd
            </span>
            <span className="text-red-600 font-medium" data-id="element-5480">
              {failCount} afgekeurd
            </span>
            <span className="text-amber-600 font-medium" data-id="element-5481">
              {reviewCount} review
            </span>
          </div>

          {batchResult && <ExportMenu results={batchResult.results.filter(r => filteredRows.some(fr => fr.questionId === r.questionId))} filePrefix="governance-vragen" data-id="element-5482" />}
        </div>
      </div>

      {/* Table / Cards */}
      <div className="space-y-2" data-id="element-5483">
        {paginatedRows.length === 0 ? <div className="rounded-xl border border-gray-200 bg-white py-12 text-center" data-id="element-5484">
            <p className="text-gray-500" data-id="element-5485">
              Geen vragen gevonden voor deze filters.
            </p>
          </div> : viewMode === 'table' ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5486">
            <div className="overflow-x-auto" data-id="element-5487">
              <table className="w-full text-left text-sm" data-id="element-5488">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-medium" data-id="element-5489">
                  <tr data-id="element-5490">
                    <th className="px-4 py-3" data-id="element-5491">ID</th>
                    <th className="px-4 py-3" data-id="element-5492">Status</th>
                    <th className="px-4 py-3" data-id="element-5493">Score</th>
                    <th className="px-4 py-3" data-id="element-5494">Issues</th>
                    <th className="px-4 py-3" data-id="element-5495">Type</th>
                    <th className="px-4 py-3" data-id="element-5496">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-5497">
                  {paginatedRows.map(row => {
                const isExpanded = expandedId === row.questionId;
                const modConfig = MODULE_CONFIG[row.moduleCode] || {
                  bgClass: 'bg-gray-500'
                };
                const statusConfig = {
                  PASS: {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    icon: CheckCircle2,
                    label: 'PASS'
                  },
                  REVIEW_NEEDED: {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    icon: AlertTriangle,
                    label: 'REVIEW'
                  },
                  FAIL: {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    icon: XCircle,
                    label: 'FAIL'
                  }
                }[row.verdict];
                const StatusIcon = statusConfig.icon;
                return <Fragment key={row.questionId} data-id="element-5498">
                        <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`} onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5499">
                          <td className="px-4 py-3" data-id="element-5500">
                            <div className="flex items-center gap-2" data-id="element-5501">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5502" />
                              <span className="font-mono font-semibold text-gray-900" data-id="element-5503">
                                {row.questionId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5504">
                            <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5505">
                              <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5506" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-5507">
                            <div className="flex items-center gap-2 w-24" data-id="element-5508">
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5509">
                                <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                            width: `${row.pipelineScore}%`
                          }} data-id="element-5510" />
                              </div>
                              <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5511">
                                {row.pipelineScore}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5512">
                            <div className="flex items-center gap-1.5 text-xs" data-id="element-5513">
                              {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5514">
                                  <XCircle className="w-3 h-3" data-id="element-5515" />{' '}
                                  {row.blockerCount}
                                </span>}
                              {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5516">
                                  <AlertTriangle className="w-3 h-3" data-id="element-5517" />{' '}
                                  {row.warningCount}
                                </span>}
                              {row.blockerCount === 0 && row.warningCount === 0 && <span className="text-gray-400" data-id="element-5518">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5519">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${row.questionType === 'unknown' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`} title={row.questionType === 'unknown' ? 'Vraag niet gevonden in content registry — mogelijk verouderde batch.' : row.questionType} data-id="element-5520">
                              {getQuestionTypeLabel(row.questionType)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-5521">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 inline" data-id="element-5522" /> : <ChevronRight className="w-4 h-4 text-gray-400 inline" data-id="element-5523" />}
                          </td>
                        </tr>
                        {isExpanded && <tr data-id="element-5524">
                            <td colSpan={6} className="p-0 border-t border-gray-100" data-id="element-5525">
                              <QuestionDetailPanel row={row} data-id="element-5526" />
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div> : paginatedRows.map(row => {
        const isExpanded = expandedId === row.questionId;
        const modConfig = MODULE_CONFIG[row.moduleCode] || {
          bgClass: 'bg-gray-500'
        };
        const statusConfig = {
          PASS: {
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            text: 'text-emerald-700',
            icon: CheckCircle2,
            label: 'PASS'
          },
          REVIEW_NEEDED: {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-700',
            icon: AlertTriangle,
            label: 'REVIEW'
          },
          FAIL: {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-700',
            icon: XCircle,
            label: 'FAIL'
          }
        }[row.verdict];
        const StatusIcon = statusConfig.icon;
        return <Card key={row.questionId} className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5527">
                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer bg-white" onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5528">
                  {/* Left: ID & Status */}
                  <div className="flex items-center gap-3 min-w-0" data-id="element-5529">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5530" />
                    <div className="flex items-center gap-3 min-w-0" data-id="element-5531">
                      <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-5532">
                        {row.questionId}
                      </span>
                      <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5533">
                        <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5534" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Right: Score & Issues */}
                  <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0" data-id="element-5535">
                    {/* Issues Summary */}
                    <div className="flex items-center gap-2 text-xs" data-id="element-5536">
                      {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5537">
                          <XCircle className="w-3 h-3" data-id="element-5538" /> {row.blockerCount}
                        </span>}
                      {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5539">
                          <AlertTriangle className="w-3 h-3" data-id="element-5540" />{' '}
                          {row.warningCount}
                        </span>}
                      {row.seedWarnings.length > 0 && <span className="flex items-center gap-1 text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded" title="Seed warnings" data-id="element-5541">
                          🌱 {row.seedWarnings.length}
                        </span>}
                    </div>

                    {/* Score Bar */}
                    <div className="hidden sm:flex items-center gap-2 w-24" data-id="element-5542">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5543">
                        <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${row.pipelineScore}%`
                  }} data-id="element-5544" />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5545">
                        {row.pipelineScore}%
                      </span>
                    </div>

                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-5546" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-5547" />}
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && <QuestionDetailPanel row={row} data-id="element-5548" />}
              </Card>;
      })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200" data-id="element-5549">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5550">
            Vorige
          </button>
          <span className="text-sm text-gray-600" data-id="element-5551">
            Pagina <span className="font-semibold text-gray-900" data-id="element-5552">{page}</span>{' '}
            van {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5553">
            Volgende
          </button>
        </div>}
    </div>;
}